import { useMemo } from 'react';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { NAME_SHEET_FUND_KEY, RELATED_ENTITY_OWNER_VALUE } from 'pages/Waterfall/constants';
import { UseFirmTotalSheetConfigProps } from './useFirmTotalSheetConfig.props';
import firmInvestmentParser from '../FirmInvestmentParser/firmInvestmentParser';
import getFirmInvestmentRowConfig from '../FirmInvestmentRowConfig/getFirmInvestmentRowConfig';

const useFirmTotalSheetConfig = ({
  securityCount,
  investmentsInPortfolioSheets,
  allInvestmentsSheets,
  weightedShareValues,
  isSelectedPortfolioPrimaryCapTable,
  fundInvestmentsSheetsNamesRef,
  waterfallHasRunAtLeastOnce,
}: UseFirmTotalSheetConfigProps) => {
  const relatedEntitiesOwnShares = useMemo(
    () => Boolean((weightedShareValues ?? []).filter(({ owner }) => owner === RELATED_ENTITY_OWNER_VALUE).length),
    [weightedShareValues]
  );

  const investmentsInPortfolioSheetNames = useMemo(
    () => investmentsInPortfolioSheets.map(sheet => sheet.name),
    [investmentsInPortfolioSheets]
  );

  // Update ref when fundInvestmentsSheets changes
  const incomingFundSheetNames = allInvestmentsSheets
    .map(sheet => sheet.name)
    .filter(name => name.endsWith(`_${NAME_SHEET_FUND_KEY}`));
  if (fundInvestmentsSheetsNamesRef.current.length !== incomingFundSheetNames.length && waterfallHasRunAtLeastOnce) {
    // this ref is shared with InputSheet to build the expression for Firm Total Exit Proceeds
    // eslint-disable-next-line no-param-reassign
    fundInvestmentsSheetsNamesRef.current = incomingFundSheetNames;
  }

  const fundInvestmentsSheetNames = fundInvestmentsSheetsNamesRef.current;

  const spreadsheet = useMemo(() => {
    const shouldNotShowSheet =
      (fundInvestmentsSheetNames.length <= 1 && !relatedEntitiesOwnShares) ||
      (fundInvestmentsSheetNames.length <= 1 && !isSelectedPortfolioPrimaryCapTable);
    if (shouldNotShowSheet) {
      return undefined;
    }
    return new SpreadsheetConfig({
      columns: [{ label: 'Share Count' }, { label: 'Value' }],
      rowConfig: getFirmInvestmentRowConfig({
        securityCount,
        fundInvestmentsSheetNames,
        investmentsInPortfolioSheetNames,
      }),
      parser: firmInvestmentParser,
      reverseParser: () => ({}) as SpreadsheetConfig<any, any, any, any>['reverseParser'], // no reverse parsing needed for this page
      showTitlesColumn: false,
      name: 'FirmInvestmentSheet',
    });
  }, [
    securityCount,
    relatedEntitiesOwnShares,
    fundInvestmentsSheetNames,
    isSelectedPortfolioPrimaryCapTable,
    investmentsInPortfolioSheetNames,
  ]);
  return {
    spreadsheet,
  };
};

export default useFirmTotalSheetConfig;
